import React, { useState } from "react";

import { Box } from "@chakra-ui/react";
import { IApiErrorData } from "api/axios/types";
import {
  IFaceVerificationPayload,
  IFaceVerificationResponse,
} from "api/compliance/types";
import { ReactComponent as IconCopy } from "assets/icons/copy.svg";
import { ReactComponent as Refresh } from "assets/icons/refresh-cw.svg";
import { useIndividualCompliance } from "contexts/individual-compliance-context";
import { IndividualComplianceQueryKeys } from "contexts/individual-compliance-context/consts";
import copy from "copy-to-clipboard";
import { useMutateData } from "hooks/useMutateData";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { Paragraph3 } from "ui/typography";

import { ComplianceStatus } from "./ComplianceStatus";
import { FaceVerificationResetModal } from "./FaceVerificationResetModal";
import { IndividualContentItem } from "./IndividualContentItem";
import { IIndividualContentItem, IndividualDetailsProps } from "./types";

export const FaceVerification = (props: IndividualDetailsProps) => {
  const [isFaceResetModalOpen, setIsFaceResetModalOpen] = useState(false);
  const {
    isContentWrapperBelow600,
    isContentWrapperBelow745,
    individual,
    hideResetFaceButton,
  } = props;
  const { kycLivenessCheck, kycJobId, kycJobStatus } = individual;
  const { setSelectedIndividualDetails } = useIndividualCompliance();

  const copyText = (text: string) => {
    if (text === "" || text === "N/A") return;
    copy(text);
    customSuccessToast({ message: `Copied: ${text}` });
  };

  const userDetails: IIndividualContentItem[] = [
    {
      title: "Liveness Check",
      content: kycLivenessCheck || "N/A",
    },
    {
      title: "Job ID",
      content: (
        <Box className="flex items-center">
          <p>{kycJobId || "N/A"}</p>
          {kycJobId && (
            <IconCopy
              onClick={() => copyText(kycJobId)}
              className="ml-1 cursor-pointer"
            />
          )}
        </Box>
      ),
    },
    {
      title: "Job Status",
      content: (
        <ComplianceStatus
          status={kycJobStatus === "success" ? "approved" : "pending"}
          text={kycJobStatus === "success" ? "Success" : "pending"}
          className="w-fit"
        />
      ),
    },
  ];

  const {
    mutateAsync: resetFaceVerification,
    isPending: isResettingFaceVerification,
  } = useMutateData<IFaceVerificationPayload, IFaceVerificationResponse>(
    [IndividualComplianceQueryKeys.FACE_VERIFICATION],
    {
      onSuccess: async (data) => {
        customSuccessToast({
          title: "Reset Face Verification",
          message: data.message,
        });
        setSelectedIndividualDetails((previousSelectedIndividualDetails) =>
          previousSelectedIndividualDetails
            ? {
                ...previousSelectedIndividualDetails,
                kycJobStatus: null,
                kycLivenessCheck: null,
                kycJobId: null,
              }
            : null
        );
      },
      onError(error) {
        const err = error as unknown as IApiErrorData;
        const message = err.message;
        customErrorToast({ title: "Individual Compliance", message });
      },
    }
  );

  const handleFaceResetSection = async () => {
    try {
      await resetFaceVerification({
        apiRoute: `/control/personal/compliance/reset-face-verification`,
        data: {
          userId: individual.id,
        },
        method: "PATCH",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
    setIsFaceResetModalOpen(false);
  };

  return individual.id ? (
    <>
      <Box className="flex flex-col gap-6">
        <Box>
          <Box
            css={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "32px",
              rowGap: "16px",
            }}
          >
            {userDetails.map((verificatioResult, index) => (
              <IndividualContentItem
                key={index}
                title={verificatioResult.title}
                content={verificatioResult.content}
                isContentWrapperBelow745={isContentWrapperBelow745}
                isContentWrapperBelow600={isContentWrapperBelow600}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="mt-4 flex items-center justify-end">
        {!hideResetFaceButton && (
          <Button
            variant="outline"
            textColor="#00535C"
            border={"1px solid #D0D5DD"}
            _hover={{
              border: "1px solid #D0D5DD",
            }}
            icon={<Refresh />}
            onClick={() => {
              setIsFaceResetModalOpen(true);
            }}
          >
            Reset
          </Button>
        )}
      </Box>
      <FaceVerificationResetModal
        handleFaceResetSection={handleFaceResetSection}
        handleToggleFaceResetModal={() =>
          setIsFaceResetModalOpen((prev) => !prev)
        }
        isLoading={isResettingFaceVerification}
        isOpen={isFaceResetModalOpen}
        setIsOpen={setIsFaceResetModalOpen}
      />
    </>
  ) : (
    <Box>
      <Paragraph3
        lineHeight="22.4px"
        textColor="#718096"
      >
        Nothing to see here yet.
      </Paragraph3>
    </Box>
  );
};
